import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Skeleton,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchRestaurants, deleteRestaurant } from "@/store/restaurantsSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import type { Restaurant } from "@/types";

export default function Restaurants() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((s) => s.restaurants);
  const { items: categories } = useAppSelector((s) => s.categories);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [deleteTarget, setDeleteTarget] = useState<Restaurant | null>(null);

  useEffect(() => {
    dispatch(fetchRestaurants());
    dispatch(fetchCategories());
  }, [dispatch]);

  const filtered = items.filter((r) => {
    const matchesSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      !selectedCategory || r.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteRestaurant(deleteTarget.id)).unwrap();
      notify(t("common.delete"), "success");
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {t("restaurants.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("restaurants.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/restaurants/new")}
        >
          {t("restaurants.add")}
        </Button>
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder={t("common.search")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ my: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Chip
          label={t("restaurants.allCategories")}
          color={!selectedCategory ? "primary" : "default"}
          onClick={() => setSelectedCategory("")}
        />
        {categories.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.name}
            color={selectedCategory === cat.id ? "primary" : "default"}
            onClick={() =>
              setSelectedCategory(selectedCategory === cat.id ? "" : cat.id)
            }
          />
        ))}
      </Box>
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            mt: 4,
            textAlign: "center",
          }}
        >
          {t("restaurants.noResults")}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((r) => (
            <Grid key={r.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  "&:hover": { boxShadow: 4 },
                  transition: "box-shadow 0.2s",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {r.name}
                      </Typography>
                      {r.category && (
                        <Chip
                          label={r.category.name}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                      {r.location && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <LocationOnIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {r.location}
                          </Typography>
                        </Box>
                      )}
                      {r.url && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <LinkIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {r.url}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/restaurants/${r.id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(r)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("common.delete")}
        message={t("restaurants.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
